import Tippy from '@tippyjs/react';
import React, { MouseEventHandler, ReactNode, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { isEmpty } from '../../../components/mplat/DataList/helpers';
import { useUploadContext } from '../../../contexts/UploadContext';
import { ReactComponent as AccessIcon } from '../../../img/storage/list-access.svg';
import { ReactComponent as ShareIcon } from '../../../img/storage/list-share.svg';
import { SdnOperationTaskStatus } from '../../../services/sdn';
import { StorageItem } from '../types';
import StorageContextMenu, { MENU_HEIGHT, MENU_WIDTH } from './StorageContextMenu';

interface StorageGridItemProps {
    item: StorageItem,
    selected?: boolean,
    onClick?: MouseEventHandler<HTMLAnchorElement>,
    onShare?: (v: string) => void
    setShowAccessModal: (v: string) => void,
    onContextMenu?: (top: number, left: number) => void,
    onDoubleClick?: (row: StorageItem) => void,
    tippy?: ReactNode,
}

function StorageGridItem({ item, tippy, selected, onClick, setShowAccessModal, onContextMenu, onDoubleClick, onShare }: StorageGridItemProps) {
    const { operations } = useUploadContext();

    // const pdf = ['.PDF'];
    const video = ['AVI', 'MP4', 'MPG', 'MPEG', 'MOV', 'MKV', 'MXF', 'HLS', 'FLV', 'WEBM'];
    const audio = ['MP3', 'WAV', 'WMA', 'AAC'];
    // const texts = ['.TXT'];
    const archives = ['.ZIP'];

    const isVideo = video.some((ext) => item.name.toUpperCase().endsWith(ext));
    const isAudio = audio.some((ext) => item.name.toUpperCase().endsWith(ext));
    const isArchive = archives.some((ext) => item.name.toUpperCase().endsWith(ext));

    const inProgress = useMemo(() => operations.some((o) => o.name === item.prefix && o.status < SdnOperationTaskStatus.RETRY), [operations]);

    const result = (
        <Link
            to={''}
            className={
                'storage__files-item'
                + (selected ? ' _selected' : '')
                + (item.linked ? ' _mediabaza' : '')
                + (item.folder ? ' _folder' : ' _file')
                + ((isVideo || isAudio) ? ' _video' : '')
                + ((isArchive) ? ' _archive' : '')
                + ((item.preSignUrl) ? ' _image' : '')
                + (inProgress ? ' _in-progress' : '')
            }
            onClick={(e) => {
                e.preventDefault();
                onClick && onClick(e);
            }}
            onDoubleClick={() => onDoubleClick && onDoubleClick(item)}
            onContextMenu={(e) => {
                e.preventDefault();
                onContextMenu && onContextMenu(e.pageY, e.pageX);
            }}
        >
            <div className="storage__files-item-icon" style={{ backgroundImage: item.preSignUrl ? 'none' : undefined }}>
                {item.preSignUrl && <img src={item.preSignUrl} alt="" />}
                <div className="storage__files-item-share">
                    {item.access && (
                        <div className="storage__files-item-share-item" onClick={() => onShare && onShare(item.prefix || '')} data-html="true" data-placement="right-end" data-interactive="true" data-id="1" aria-expanded="false">
                            <ShareIcon />
                        </div>
                    )}
                    {item.shared &&
                        // <Tippy
                        //     content={<div className="storage__files-item-share-access">
                        //         <div className="storage__files-item-share-access-label">
                        //             <Trans>storage:ACCESS_SHARE_OPEN</Trans>
                        //         </div>
                        //         <div className="storage__files-item-share-access-cta _js-storage-access-tippy" onClick={() => }>
                        //             <Trans>Details</Trans>
                        //         </div>
                        //     </div>}
                        //     placement="right-end"
                        //     animation="shift-away"
                        //     theme="storage"
                        //     arrow={false}
                        //     duration={[100, 100]}
                        //     allowHTML={true}
                        //     interactive={true}
                        //     appendTo={() => document.body}
                        // >
                        <div className="storage__files-item-share-item" onClick={() => setShowAccessModal(item.prefix || '')}>
                            <AccessIcon />
                        </div>
                        // </Tippy>
                    }
                </div>
            </div>
            <div className="storage__files-item-name">{item.name}</div>
        </Link>
    );

    return tippy ?
        (
            <Tippy
                arrow={false}
                content={tippy}
                placement="right-end"
                animation="shift-away"
                duration={[100, 100]}
                theme="storage"
                allowHTML={true}
            >
                {result}
            </Tippy>
        )
        :
        result;
}

interface StorageGridProps {
    data: StorageItem[],
    selected: string[],
    setSelected: (v: string[]) => void,
    onDoubleClick?: (row: StorageItem) => void,
    children?: ReactNode,
    tippy?: (item: StorageItem) => ReactNode,
    groupBy?: (row: StorageItem) => string,
    onDownloadZip?: (v: string) => void
    onDownload?: (v: string[]) => void,
    onShare?: (v: string) => void
    onAccess?: (v: string) => void
    onCopy?: (v: string[]) => void
    onCopyInMy?: (v: string[]) => void
    onMove?: (v: string[]) => void
    onDelete?: (v: string[]) => void
    onRename?: (v: string) => void
    onUnzip?: (v: string) => void
    onZip?: (v: string[]) => void,
    menuFull?: boolean
    forbidDelete?: boolean,
    selectionMode?: 'single' | 'multi',
}

function StorageGrid({
    children, selected, setSelected, data, selectionMode,
    tippy, groupBy, onAccess, onCopy,
    onDelete, onDownload, onMove, onShare, onRename, onUnzip, onDoubleClick, onZip, onCopyInMy, onDownloadZip,
    forbidDelete, menuFull, ...props
}: StorageGridProps) {
    const refList = useRef<HTMLDivElement | null>(null);

    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    // const [containerPosition, setContainerPosition] = useState({ top: 0, left: 0 });
    const [menuOpen, setMenuOpen] = useState(false);
    // const [accessPath, setAccessPath] = useState<string>('');
    // const [showAccessModal, setShowAccessModal] = useState(false);

    // useEffect(() => {
    //     const rect = refList.current?.getBoundingClientRect();
    //     const top = (rect?.top || 0) + document.documentElement.scrollTop;
    //     const left = (rect?.left || 0) + document.documentElement.scrollLeft;
    //     setContainerPosition({ top, left });
    // }, [menuOpen]);

    return (
        <div ref={refList} className="storage__files" {...props}>
            {/* {showAccessModal && <StorageAccessModal
                isOpen={showAccessModal}
                onClose={() => setShowAccessModal(false)}
                path={accessPath}
            />} */}
            {data?.map((item, i) => {
                const thisGroup = groupBy && groupBy(item);
                let group = undefined;
                if (groupBy && thisGroup !== undefined) {
                    if (i === 0) {
                        group = thisGroup;
                    } else {
                        const previousValue = data[i - 1];
                        const prevGroup = groupBy(previousValue);
                        if (prevGroup !== thisGroup) {
                            group = thisGroup;
                        }
                    }
                }
                const key = item.prefix;
                const id = isEmpty(key) ? i : key;

                return (
                    <React.Fragment key={id}>
                        {group && <div className="storage__files-heading">{group}</div>}
                        <StorageGridItem
                            tippy={tippy && tippy(item)}
                            key={id}
                            item={item}
                            selected={selected.includes(key as string)}
                            setShowAccessModal={(s) => { onAccess && onAccess(s); /*setAccessPath && setAccessPath(s); setShowAccessModal && setShowAccessModal(true);*/ }}
                            onShare={onShare}
                            onClick={(e) => {
                                if ((e.ctrlKey || e.metaKey) && selectionMode !== 'single') {
                                    setSelected(selected.includes(key as string) ? selected.filter((s) => s !== key) : [...selected, key as string])
                                } else if (e.shiftKey && selectionMode !== 'single') {
                                    const positions = selected.map((s) => data.findIndex((r) => r.prefix === s));
                                    const max = Math.max(...positions);
                                    const min = Math.min(...positions);
                                    // const lastSelected = selected[selected.length - 1];
                                    // const lastPos = data.findIndex((r) => prop(r as TableRow, keyField) as string === lastSelected);
                                    const pos = i;
                                    if (min !== Infinity && max !== -Infinity) {
                                        if (pos >= min && pos <= max) {
                                            if (selected.includes(key as string)) {
                                                setSelected(selected.filter((s) => s !== key));
                                            } else {
                                                setSelected([...selected, key as string]);
                                            }
                                        } else {
                                            if (pos < min) {
                                                const slice = data.slice(pos, max + 1);
                                                setSelected(slice.map((z) => z.prefix || ''));
                                            } else {
                                                const slice = data.slice(min, pos + 1);
                                                setSelected(slice.map((z) => z.prefix || ''));
                                            }
                                        }
                                    } else if (min !== Infinity) {
                                        const slice = pos > max ? data.slice(max, pos + 1) : data.slice(pos, max + 1);
                                        setSelected(slice.map((z) => z.prefix || ''));
                                    } else if (max !== -Infinity) {
                                        const slice = pos > min ? data.slice(min, pos + 1) : data.slice(pos, min + 1);
                                        setSelected(slice.map((z) => z.prefix || ''));
                                    } else {
                                        setSelected([key as string]);
                                    }
                                } else {
                                    if (selected.length > 1) {
                                        setSelected([key as string])
                                    } else {
                                        setSelected(selected.includes(key as string) ? [] : [key as string])
                                    }
                                }
                            }}
                            onContextMenu={(t, l) => {
                                if (selected && selected.length < 2) {
                                    setSelected([key as string]);
                                }
                                setMenuPosition({ top: t, left: l });
                                setMenuOpen(true);
                            }}
                            onDoubleClick={onDoubleClick}
                        />
                    </React.Fragment>
                )
            })}
            {menuOpen && createPortal(<StorageContextMenu
                selected={selected}
                top={menuPosition.top}
                left={menuPosition.left}
                isOpen={menuOpen}
                onClose={() => { setMenuOpen(false); }}
                onDownloadZip={onDownloadZip}
                onDownload={onDownload}
                onAccess={onAccess}
                onCopy={onCopy}
                onCopyInMy={onCopyInMy}
                onDelete={onDelete}
                onMove={onMove}
                onShare={onShare}
                onRename={onRename}
                onUnzip={onUnzip}
                onZip={onZip}
                menuFull={menuFull}
                forbidDelete={forbidDelete}
                className={`
                    ${window.innerWidth < menuPosition.left + MENU_WIDTH ? '_left' : ''}
                    ${window.innerHeight < menuPosition.top + MENU_HEIGHT ? '_top' : ''}
                `}
            />, document.body)}

            {children}
        </div>
    );
}

export default StorageGrid;
